import React, { useMemo } from 'react';
import './GeoChecklistPage.scss';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { getProjectsFromAccount } from '../../shared/helpers/project/ProjectHelper';
import { GEO_CHECKLIST, PROJECTS } from '../../shared/routes/path';
import Title from '../../shared/components/title/Title';
import Tabs from '../../shared/components/tabs/Tabs';
import { CHECKLIST_TABS } from './data/checklistData';
import { useGetGeoChecklist } from '../../shared/api/hooks/geo-checklist/useGetGeoChecklist';
import { useToggleGeoChecklistTask } from '../../shared/api/hooks/geo-checklist/useToggleGeoChecklistTask';
import { useResetGeoChecklist } from '../../shared/api/hooks/geo-checklist/useResetGeoChecklist';
import ChecklistIntro from './components/ChecklistIntro';
import ChecklistProgressBar from './components/ChecklistProgressBar';
import ChecklistSection from './components/ChecklistSection';
import ChecklistPdfExport from './components/ChecklistPdfExport';

const GeoChecklistPage: React.FC = () => {
  const { projectId, tab: tabSlug } = useParams<{ projectId: string; tab: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { projectResponse, accountResponse } = useAccount();

  const projectIdNumber = projectId ? Number(projectId) : null;

  const projects = useMemo(
    () => getProjectsFromAccount(accountResponse, projectResponse ?? []),
    [accountResponse, projectResponse]
  );

  const currentProject = useMemo(
    () => projects.find((p) => p.id === projectIdNumber) ?? null,
    [projects, projectIdNumber]
  );

  const visibleTabs = useMemo(() => CHECKLIST_TABS.filter((tab) => !tab.hidden), []);

  const activeTab = useMemo(
    () => visibleTabs.find((tab) => tab.slug === tabSlug) ?? visibleTabs[0],
    [tabSlug, visibleTabs]
  );

  const { data: checklistData } = useGetGeoChecklist(projectIdNumber);
  const toggleTask = useToggleGeoChecklistTask();
  const resetChecklist = useResetGeoChecklist();

  const checkedTasks: Record<string, string> = useMemo(() => {
    if (!checklistData || !activeTab) return {};
    return checklistData[activeTab.type] ?? {};
  }, [checklistData, activeTab]);

  const globalProgress = useMemo(() => {
    if (!activeTab) return { checked: 0, total: 0, percent: 0 };
    const allTaskIds = activeTab.sections.flatMap((s) => s.tasks.map((t) => t.id));
    const total = allTaskIds.length;
    const checked = allTaskIds.filter((id) => id in checkedTasks).length;
    const percent = total > 0 ? Math.round((checked / total) * 100) : 0;
    return { checked, total, percent };
  }, [activeTab, checkedTasks]);

  const tabItems = useMemo(
    () =>
      visibleTabs.map((tab) => ({
        url: `${GEO_CHECKLIST}/${projectIdNumber}/${tab.slug}`,
        label: t(tab.labelKey),
        isActive: tab.slug === tabSlug,
      })),
    [projectIdNumber, tabSlug, t, visibleTabs]
  );

  const handleToggle = (taskId: string) => {
    if (!projectIdNumber) return;
    toggleTask.mutate({
      projectId: projectIdNumber,
      checklistType: activeTab.type,
      taskId,
    });
  };

const confirmReset = () => {
    confirmDialog({
      header: t('geoChecklist.resetConfirmTitle'),
      message: t('geoChecklist.resetConfirmMessage'),
      acceptLabel: t('geoChecklist.resetConfirmYes'),
      rejectLabel: t('geoChecklist.resetConfirmNo'),
      accept: () => {
        if (!projectIdNumber) return;
        resetChecklist.mutate({
          projectId: projectIdNumber,
          checklistType: activeTab.type,
        });
      },
    });
  };

  const handleProjectChange = (e: { value: typeof currentProject }) => {
    if (!e.value) return;
    navigate(`${GEO_CHECKLIST}/${e.value.id}/${tabSlug}`);
  };

  if (!accountResponse || !activeTab) {
    return null;
  }

  if (!projectIdNumber || (!currentProject && projects.length > 0)) {
    return <Navigate to={PROJECTS} replace />;
  }

  return (
    <div className="geo-checklist-page page-content">
      <ConfirmDialog className="dialog-box" />
      <div className="page-box">
        <div className="page-box__filters" data-observe-sticky>
          <Title
            title={t('geoChecklist.title')}
            childrenOnMain={<div className="title-box__subtitle">{t('geoChecklist.subtitle')}</div>}
            children={
              <div className="filters">
                <div className="filters-panel">
                  <Dropdown
                    value={currentProject}
                    onChange={handleProjectChange}
                    options={projects}
                    optionLabel="projectLabel"
                    className="dropdown"
                  />
                  <ChecklistPdfExport
                    projectName={currentProject?.projectLabel ?? ''}
                    activeTab={activeTab}
                    checkedTasks={checkedTasks}
                  />
                  <Button
                    className="action-btn"
                    onClick={confirmReset}
                    title={t('geoChecklist.resetChecklist')}
                  >
                    <span className="ico--trash ico--xs" />
                  </Button>
                </div>
              </div>
            }
          />
        </div>

        {visibleTabs.length > 1 && (
          <div className="page-box__tabs">
            <Tabs items={tabItems} additionalClass="" />
          </div>
        )}

        <div className="page-box__content">
          <ChecklistIntro tab={activeTab} />
          <ChecklistProgressBar
            checked={globalProgress.checked}
            total={globalProgress.total}
            percent={globalProgress.percent}
          />
          {activeTab.sections.map((section, idx) => (
            <ChecklistSection
              key={section.id}
              section={section}
              checkedTasks={checkedTasks}
              onToggle={handleToggle}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export { GeoChecklistPage };
export default GeoChecklistPage;
